import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, AlertTriangle } from "lucide-react";
import { useHousehold } from "@/hooks/useHousehold";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import PaywallScreen from "./PaywallScreen";

/**
 * Wraps content routes. Shows PaywallScreen if the household has no active
 * subscription. billing_exempt users always pass through.
 *
 * Critical guarantees:
 * - NEVER show the paywall while household data is loading or errored.
 * - On post-Stripe-checkout return, poll briefly for webhook activation.
 * - On entitlement-fetch failure, show a recoverable error UI with retry +
 *   sign-out, NOT the paywall (which is the "false paywall screen" bug).
 * - Hard timeout so loading never hangs forever.
 */
export default function SubscriptionGate({ children }: { children: React.ReactNode }) {
  const { isSubscribed, loading, loadError, refetch, household } = useHousehold();
  const { signOut } = useAuth();
  const [timedOut, setTimedOut] = useState(false);
  const [activating, setActivating] = useState(false);
  const [linkingFamily, setLinkingFamily] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const billingFlag = searchParams.get("billing");
  const pollingRef = useRef(false);
  const familyPollRef = useRef(false);

  // If a child just signed up and the household membership hasn't propagated
  // yet, poll briefly before defaulting to the paywall. Without this the
  // child can see a paywall flash between auth state change and the invite
  // claim's INSERT becoming visible to the next useHousehold load.
  useEffect(() => {
    if (loading || isSubscribed || household || familyPollRef.current) return;
    // Was the user just in a Join Family signup? If localStorage still has
    // the invite key (claim in flight) OR the user just authenticated and
    // we haven't found a household yet, poll for a short window.
    const justJoined = !!localStorage.getItem("heirwise_invite_code");
    if (!justJoined) return;
    familyPollRef.current = true;
    setLinkingFamily(true);
    let attempts = 0;
    const maxAttempts = 8;
    const interval = setInterval(async () => {
      attempts += 1;
      try { await refetch(); } catch { /* non-critical: failure is safe to ignore here */ }
      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setLinkingFamily(false);
        familyPollRef.current = false;
      }
    }, 1200);
    return () => {
      clearInterval(interval);
      setLinkingFamily(false);
      familyPollRef.current = false;
    };
  }, [loading, isSubscribed, household, refetch]);

  // Safety valve: 8s max wait on initial load so we don't trap the user.
  useEffect(() => {
    if (!loading) {
      setTimedOut(false);
      return;
    }
    const timer = setTimeout(() => {
      console.warn("[SubscriptionGate] Loading timed out after 8s");
      setTimedOut(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, [loading]);

  // After Stripe checkout success, poll up to 10 times for the subscription to activate.
  useEffect(() => {
    if (billingFlag !== "success" || isSubscribed || pollingRef.current) return;
    pollingRef.current = true;
    setActivating(true);
    let attempts = 0;
    const maxAttempts = 10;
    const tick = async () => {
      attempts += 1;
      try {
        await refetch();
      } catch (err) {
        console.warn("[SubscriptionGate] post-checkout refetch failed:", err);
      }
      if (attempts >= maxAttempts) {
        setActivating(false);
        pollingRef.current = false;
      }
    };
    const interval = setInterval(() => {
      if (isSubscribed || attempts >= maxAttempts) {
        clearInterval(interval);
        setActivating(false);
        pollingRef.current = false;
      } else {
        void tick();
      }
    }, 1500);
    void tick();
    return () => clearInterval(interval);
  }, [billingFlag, isSubscribed, refetch]);

  // Activating state after Stripe return — do NOT flash the paywall
  if (activating && !isSubscribed) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <p className="text-muted-foreground text-sm">Activating your subscription…</p>
      </div>
    );
  }

  // Linking a child to their family — do NOT flash the paywall
  if (linkingFamily && !isSubscribed) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-background">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
        <p className="text-muted-foreground text-sm">Linking you to your family…</p>
      </div>
    );
  }

  // Initial loading
  if (loading && !timedOut) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  // CRITICAL: if loading errored OR timed out without resolving, show a
  // recoverable error UI. We MUST NOT default to the paywall here — that's
  // the "incorrectly shown paywall" bug for paid users with a transient
  // network/edge-function failure.
  if (loadError || (timedOut && !isSubscribed)) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <AlertTriangle className="w-10 h-10 text-destructive" />
        <div className="space-y-1 max-w-sm">
          <h2 className="text-lg font-semibold text-foreground">We couldn't verify your account</h2>
          <p className="text-sm text-muted-foreground">
            Your access info didn't load. This is usually a network hiccup —
            try again, or sign out and back in.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 w-full max-w-xs">
          <Button onClick={() => { setTimedOut(false); void refetch(); }} className="flex-1">
            Try again
          </Button>
          <Button
            variant="outline"
            onClick={async () => { await signOut(); navigate("/login", { replace: true }); }}
            className="flex-1"
          >
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  if (isSubscribed) return <>{children}</>;

  return <PaywallScreen />;
}
